import dd from 'dingtalk-jsapi'
import { IBizUtilScanResult } from 'dingtalk-jsapi/api/biz/util/scan'
import { ENV_ENUM } from 'dingtalk-jsapi/lib/sdk'

// 钉钉环境检测
export const isDingTalkEnvironment = (): boolean => {
  return dd.env.platform !== ENV_ENUM.notInDingTalk
}

// 是IOS吗
export const isIOS = (): boolean => {
  return dd.env.platform === ENV_ENUM.ios
}

// 是安卓吗
export const isANDROID = (): boolean => {
  return dd.env.platform === ENV_ENUM.android
}

// 是PC吗
export const isPC = (): boolean => {
  return dd.env.platform === ENV_ENUM.pc
}

// 是移动端吗（iOS或Android）
export const isMobile = (): boolean => {
  return isIOS() || isANDROID()
}

// 获取钉钉环境信息
export const getDingTalkEnv = () => {
  return {
    platform: dd.env.platform,
    version: dd.env.version
  }
}

// 钉钉JSAPI初始化配置
export const initDingTalkJSAPI = async (): Promise<void> => {
  return new Promise((resolve, reject) => {
    dd.config({
      agentId: import.meta.env.VITE_DINGTALK_AGENT_ID,
      corpId: import.meta.env.VITE_DINGTALK_CORP_ID,
      timeStamp: Date.now(),
      nonceStr: Math.random().toString(36).substring(7),
      signature: '', // 这里需要后端提供签名
      type: 0,
      jsApiList: [
        'runtime.info',
        'runtime.permission.requestAuthCode',
        'device.notification.confirm',
        'device.notification.alert',
        'device.audio',
        'util.openLink'
      ]
    })

    dd.ready(() => {
      console.log('DingTalk JSAPI 初始化成功')
      resolve()
    })

    dd.error((error: unknown) => {
      console.error('DingTalk JSAPI 初始化失败:', error)
      reject(error)
    })
  })
}

// 获取免登授权码
export const getAuthCode = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    const requestAuthCode = dd.runtime.permission.requestAuthCode as {
      (options: {
        corpId: string
        onSuccess: (result: { code: string }) => void
        onFail: (error: unknown) => void
      }): void
    }

    requestAuthCode({
      corpId: import.meta.env.VITE_DINGTALK_CORP_ID,
      onSuccess: (result: { code: string }) => {
        resolve(result.code)
      },
      onFail: (error: unknown) => {
        reject(error)
      }
    })
  })
}

// 显示钉钉通知
export const showDingTalkToast = (
  type: 'success' | 'error',
  message: string,
  duration: number = 3
): Promise<void> => {
  return new Promise(() => {
    const toast = dd.device.notification.toast as {
      (options: {
        icon: 'success' | 'error'
        type: 'success' | 'error'
        duration?: number
        text: string
      }): void
    }
    toast({
      icon: type,
      type: type,
      duration: duration,
      text: message
    })
  })
}

// 显示钉钉提示框
export const showDingTalkAlert = (message: string): Promise<void> => {
  return new Promise((resolve) => {
    const alert = dd.device.notification.alert as {
      (options: {
        message: string
        title: string
        buttonName: string
        onSuccess: () => void
        onFail: () => void
      }): void
    }

    alert({
      message: message,
      title: '提示',
      buttonName: '确定',
      onSuccess: () => {
        resolve()
      },
      onFail: () => {
        resolve()
      }
    })
  })
}

// 显示钉钉确认框
export const showDingTalkConfirm = (message: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const confirm = dd.device.notification.confirm as {
      (options: {
        message: string
        title: string
        buttonLabels: string[]
        onSuccess: (result: { buttonIndex: number }) => void
        onFail: () => void
      }): void
    }

    confirm({
      message: message,
      title: '确认',
      buttonLabels: ['取消', '确定'],
      onSuccess: (result: { buttonIndex: number }) => {
        resolve(result.buttonIndex === 1)
      },
      onFail: () => {
        resolve(false)
      }
    })
  })
}

// 在钉钉中打开链接
export const openLinkInDingTalk = (url: string): void => {
  if (dd.biz && dd.biz.util && dd.biz.util.openLink) {
    const openLink = dd.biz.util.openLink as {
      (options: {
        url: string
        onSuccess: () => void
        onFail: (error: unknown) => void
      }): void
    }

    openLink({
      url: url,
      onSuccess: () => {
        console.log('链接打开成功')
      },
      onFail: (error: unknown) => {
        console.error('链接打开失败:', error)
      }
    })
  } else {
    // 降级处理，直接打开链接
    window.open(url, '_blank')
  }
}

// 钉钉调用摄像头扫描
export const scanQRCodeInDingTalk = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (dd.biz && dd.biz.util && dd.biz.util.scan) {
      const scan = dd.biz.util.scan as {
        (options: {
          onSuccess: (result: IBizUtilScanResult) => void
          onFail: () => void
          onCancel: () => void
        }): void
      }
      
      scan({
        onSuccess: (result) => {
          const qrcode: string = result.text
          if (qrcode) {
            resolve(qrcode)
          } else {
            reject(new Error('扫描结果为空'))
          }
        },
        onFail: () => {
          reject(new Error('扫描失败'))
        },
        onCancel: () => {
          reject(new Error('用户取消扫描'))
        }
      })
    } else {
      reject(new Error('钉钉扫码功能不可用'))
    }
  })
}